import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

export default function Auth() {
  const navigate = useNavigate();
  const { login, register, loginWithGoogle } = useAuth();
  const [mode, setMode] = useState("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const isSignup = mode === "signup";

  const submit = async (e) => {
    e.preventDefault();
    if (isSignup && !consent) {
      toast.error("Please tick the consent box to continue.");
      return;
    }
    setLoading(true);
    try {
      if (isSignup) {
        await register(email, password, consent);
        navigate("/onboarding");
      } else {
        await login(email, password);
        navigate("/care");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex justify-center bg-[#E9EEF4]">
      <div className="relative w-full max-w-[430px] min-h-screen bg-[#F8FAFC] flex flex-col px-6 py-6">
        <button
          data-testid="auth-back"
          onClick={() => navigate("/welcome")}
          aria-label="Back to welcome"
          className="min-h-[44px] min-w-[44px] -ml-2 flex items-center text-[#0C1B33]"
        >
          <ChevronLeft size={28} />
        </button>

        <div className="mt-4 mb-8">
          <h1 className="font-head text-3xl font-extrabold text-[#0C1B33]">
            {isSignup ? "Create your account" : "Welcome back"}
          </h1>
          <p className="text-slate-600 mt-2">
            {isSignup
              ? "Set up PocketDocta for expert second opinions and your health, organised."
              : "Sign in to your PocketDocta account."}
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5 flex-1">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              data-testid="auth-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="min-h-[56px] rounded-xl text-base bg-white"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              data-testid="auth-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              className="min-h-[56px] rounded-xl text-base bg-white"
            />
          </div>

          {isSignup && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 flex gap-3">
              <Checkbox
                id="consent"
                data-testid="consent-checkbox"
                checked={consent}
                onCheckedChange={(v) => setConsent(!!v)}
                className="mt-1 h-6 w-6 shrink-0 data-[state=checked]:bg-[#1D9E75] data-[state=checked]:border-[#1D9E75]"
              />
              <Label htmlFor="consent" className="text-sm leading-relaxed text-slate-700 font-normal cursor-pointer">
                I consent to PocketDocta processing my health information to provide specialist
                second opinions and health guidance. This is sensitive data and is stored encrypted.
              </Label>
            </div>
          )}

          <Button
            type="submit"
            data-testid="auth-submit"
            disabled={loading}
            className="w-full min-h-[56px] rounded-xl bg-[#1D9E75] hover:bg-[#16825F] text-white text-lg font-semibold"
          >
            {loading ? "Please wait…" : isSignup ? "Create account" : "Sign in"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-5">
          <span className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-slate-400 uppercase tracking-widest">or</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        <button
          type="button"
          data-testid="google-signin-btn"
          onClick={loginWithGoogle}
          className="w-full min-h-[56px] rounded-xl bg-white border-2 border-slate-200 text-[#0C1B33] font-semibold flex items-center justify-center gap-3 hover:border-slate-300 transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.4l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.4 36.2 44 30.6 44 24c0-1.3-.1-2.3-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>

        <button
          data-testid="auth-toggle"
          onClick={() => setMode(isSignup ? "login" : "signup")}
          className="mt-6 text-center text-[#16825F] font-semibold min-h-[44px]"
        >
          {isSignup ? "Already have an account? Sign in" : "New to PocketDocta? Create an account"}
        </button>

        <p className="mt-4 text-center text-xs text-slate-400 leading-relaxed">
          By continuing you agree to our{" "}
          <button data-testid="auth-terms-link" onClick={() => navigate("/terms")} className="underline underline-offset-2">Terms</button>
          {" "}and{" "}
          <button data-testid="auth-privacy-link" onClick={() => navigate("/privacy")} className="underline underline-offset-2">Privacy Policy</button>.
        </p>
      </div>
    </div>
  );
}
